import ExcelJS from 'exceljs';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Trigger a browser download for an in-memory buffer. */
function downloadBuffer(buffer: ArrayBuffer | Uint8Array, filename: string, mime: string) {
  const blob = new Blob([buffer as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Apply a row of objects to a worksheet, using the first row's keys as column headers. */
function sheetFromObjects(ws: ExcelJS.Worksheet, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  ws.columns = headers.map(h => ({ header: h, key: h }));
  for (const row of rows) {
    ws.addRow(row);
  }
}

// ── Export ────────────────────────────────────────────────────────────────────

/** Convert an array of objects to an .xlsx file and trigger browser download. */
export async function exportToExcel(rows: Record<string, unknown>[], filename: string) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Data');
  sheetFromObjects(ws, rows);
  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(
    buffer,
    `${filename}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

/** Convert an array of objects to a .csv file and trigger browser download. */
export async function exportToCsv(rows: Record<string, unknown>[], filename: string) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Data');
  sheetFromObjects(ws, rows);
  const buffer = await wb.csv.writeBuffer();
  downloadBuffer(buffer, `${filename}.csv`, 'text/csv;charset=utf-8;');
}

// ── Import ────────────────────────────────────────────────────────────────────

export interface ParseResult {
  headers: string[];
  rows: Record<string, string>[];
  errors: string[];
}

/** Read an .xlsx or .csv file from a browser File object and return parsed rows. */
export async function parseExcelFile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer();
  const wb = new ExcelJS.Workbook();
  const isCsv = file.name.toLowerCase().endsWith('.csv');

  try {
    if (isCsv) {
      // ExcelJS CSV reader expects a stream — Blob stream works in browsers
      // but its types are awkward, so we fall back to manual parse for CSV.
      const text = new TextDecoder().decode(buffer);
      return parseCsvText(text);
    }
    await wb.xlsx.load(buffer);
  } catch {
    throw new Error('Could not parse file. Make sure it is a valid .xlsx or .csv file.');
  }

  const ws = wb.worksheets[0];
  if (!ws) {
    return { headers: [], rows: [], errors: ['File has no worksheets.'] };
  }

  const headerRow = ws.getRow(1);
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: false }, cell => {
    headers.push(String(cell.value ?? '').trim());
  });
  if (headers.length === 0) {
    return { headers: [], rows: [], errors: ['File is empty or has no header row.'] };
  }

  const rows: Record<string, string>[] = [];
  for (let r = 2; r <= ws.rowCount; r++) {
    const row = ws.getRow(r);
    if (!row.hasValues) continue;
    const obj: Record<string, string> = {};
    let nonEmpty = false;
    headers.forEach((h, i) => {
      const cell = row.getCell(i + 1);
      const v = cellToString(cell.value);
      obj[h] = v;
      if (v) nonEmpty = true;
    });
    if (nonEmpty) rows.push(obj);
  }

  if (rows.length === 0) {
    return { headers, rows: [], errors: ['File is empty or has no data rows.'] };
  }
  return { headers, rows, errors: [] };
}

/** Convert any ExcelJS cell value into a plain string. */
function cellToString(v: ExcelJS.CellValue): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'TRUE' : 'FALSE';
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'object') {
    // Hyperlink / rich text / formula result objects
    if ('text' in v && typeof v.text === 'string') return v.text.trim();
    if ('result' in v && v.result !== undefined && v.result !== null) return String(v.result).trim();
    if ('richText' in v && Array.isArray(v.richText)) {
      return v.richText.map(rt => rt.text).join('').trim();
    }
  }
  return String(v).trim();
}

/** Minimal CSV parser — handles double-quoted fields with embedded commas/quotes. */
function parseCsvText(text: string): ParseResult {
  // Strip BOM if present
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const lines = splitCsvLines(text);
  if (lines.length === 0) {
    return { headers: [], rows: [], errors: ['File is empty.'] };
  }
  const headers = parseCsvRow(lines[0]).map(s => s.trim());
  if (headers.length === 0 || headers.every(h => !h)) {
    return { headers: [], rows: [], errors: ['File has no header row.'] };
  }
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = parseCsvRow(lines[i]);
    if (cells.every(c => !c)) continue;
    const obj: Record<string, string> = {};
    headers.forEach((h, j) => { obj[h] = (cells[j] ?? '').trim(); });
    rows.push(obj);
  }
  if (rows.length === 0) {
    return { headers, rows: [], errors: ['File is empty or has no data rows.'] };
  }
  return { headers, rows, errors: [] };
}

function splitCsvLines(text: string): string[] {
  const lines: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      cur += ch;
      inQ = !inQ;
      continue;
    }
    if ((ch === '\n' || ch === '\r') && !inQ) {
      if (cur || lines.length > 0) lines.push(cur);
      cur = '';
      // skip the \n in \r\n
      if (ch === '\r' && text[i + 1] === '\n') i++;
      continue;
    }
    cur += ch;
  }
  if (cur) lines.push(cur);
  return lines;
}

function parseCsvRow(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else                            { inQ = !inQ; }
      continue;
    }
    if (ch === ',' && !inQ) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out;
}

// ── Column auto-matching ──────────────────────────────────────────────────────

/** Normalise a header string for fuzzy matching: lowercase, strip spaces/underscores/hyphens/dots */
function normalise(s: string) {
  return s.toLowerCase().replace(/[\s_./-]+/g, '');
}

/**
 * Common aliases companies use for standard fields.
 * Key = normalised VantixMT field name, values = normalised aliases.
 */
const ALIASES: Record<string, string[]> = {
  assettag:            ['tag', 'assetid', 'assetno', 'assetnumber', 'equipmenttag', 'equipmentid', 'code', 'id'],
  name:                ['description', 'equipmentname', 'partname', 'itemname', 'title'],
  type:                ['category', 'equipmenttype', 'class', 'kind'],
  location:            ['area', 'plant', 'building', 'site', 'room', 'zone'],
  criticality:         ['priority', 'importance', 'criticallevel', 'criticalitylevel', 'criticalityrating', 'importancelevel'],
  manufacturer:        ['make', 'brand', 'mfr', 'mfg', 'vendor'],
  model:               ['modelnumber', 'modelno', 'modelnum', 'partnumber', 'partno'],
  serialnumber:        ['serial', 'serialno', 'sn', 'serialnum'],
  installdate:         ['installationdate', 'dateinstalled', 'installeddate', 'purchasedate', 'commissiondate', 'commissioningdate'],
  partnumber:          ['partno', 'itemno', 'itemnumber', 'sku', 'code', 'ref', 'reference'],
  stockquantity:       ['qty', 'quantity', 'stock', 'onhand', 'currentstock', 'qtyonhand'],
  reorderthreshold:    ['reorder', 'minstock', 'minimumstock', 'reorderpoint', 'minqty', 'reorderqty'],
  unitcost:            ['cost', 'price', 'unitprice', 'costperunit'],
  supplier:            ['vendor', 'suppliervendor', 'source'],
  // PM import aliases
  equipmentref:        ['equipment', 'asset', 'assettag', 'serial', 'serialnumber', 'unit'],
  frequencyphrase:     ['frequency', 'interval', 'schedule', 'cycle', 'every'],
  tasktype:            ['type', 'kind', 'category'],
  technicianref:       ['assignedto', 'assignee', 'technician', 'tech', 'owner'],
  estimateddurationminutes: ['duration', 'time', 'esttime', 'minutes'],
  instructions:        ['notes', 'details', 'procedure', 'steps'],
  lastcompletedat:     ['lastdone', 'lastcompleted', 'lastpm'],
  nextdueat:           ['nextdue', 'duedate', 'nextpm'],
};

/**
 * Given the target field keys and the uploaded file headers,
 * return a best-guess mapping of fieldKey → fileHeader.
 * Returns empty string when no match found.
 */
export function autoMatch(
  fieldKeys: string[],
  fileHeaders: string[],
): Record<string, string> {
  const normHeaders = fileHeaders.map(h => ({ original: h, norm: normalise(h) }));

  return Object.fromEntries(
    fieldKeys.map(key => {
      const normKey = normalise(key);
      const aliases = [normKey, ...(ALIASES[normKey] ?? [])];

      const match = normHeaders.find(h => aliases.includes(h.norm));
      return [key, match?.original ?? ''];
    }),
  );
}

// ── Template generators ───────────────────────────────────────────────────────

export async function downloadEquipmentTemplate() {
  const wb = new ExcelJS.Workbook();

  // Sheet 1: data with one sample row
  const wsData = wb.addWorksheet('Equipment Data');
  wsData.columns = [
    { header: 'asset_tag',     key: 'asset_tag',     width: 14 },
    { header: 'name',          key: 'name',          width: 22 },
    { header: 'type',          key: 'type',          width: 12 },
    { header: 'location',      key: 'location',      width: 26 },
    { header: 'criticality',   key: 'criticality',   width: 11 },
    { header: 'manufacturer',  key: 'manufacturer',  width: 14 },
    { header: 'model',         key: 'model',         width: 12 },
    { header: 'serial_number', key: 'serial_number', width: 16 },
    { header: 'install_date',  key: 'install_date',  width: 14 },
  ];
  wsData.addRow({
    asset_tag:     'PUMP-001',
    name:          'Main Water Pump',
    type:          'pump',
    location:      'Building A - Room 101',
    criticality:   'high',
    manufacturer:  'Grundfos',
    model:         'CM5-6',
    serial_number: 'SN-123456',
    install_date:  '2024-01-15',
  });

  // Sheet 2: instructions / valid values
  const wsInstr = wb.addWorksheet('Instructions');
  wsInstr.columns = [
    { header: 'Column',                width: 16 },
    { header: 'Required?',             width: 10 },
    { header: 'Valid Values / Notes',  width: 70 },
  ];
  const instructions: [string, string, string][] = [
    ['asset_tag',     'Optional', 'Unique asset ID e.g. PUMP-001. Leave blank to auto-generate from serial number.'],
    ['name',          'Required', 'Display name for the equipment'],
    ['type',          'Required', 'pump | motor | conveyor | compressor | fan | heat_exchanger | boiler | generator | other'],
    ['location',      'Required', 'Physical location e.g. "Building A, Floor 2"'],
    ['criticality',   'Optional', 'low | medium | high | critical (defaults to medium if blank)'],
    ['manufacturer',  'Optional', 'Manufacturer / brand name'],
    ['model',         'Optional', 'Model number'],
    ['serial_number', 'Optional', 'Serial number — used as asset_tag fallback when asset_tag is blank'],
    ['install_date',  'Optional', 'Installation date in YYYY-MM-DD format e.g. 2024-01-15'],
  ];
  for (const row of instructions) wsInstr.addRow(row);
  wsInstr.addRow([]);
  wsInstr.addRow(['Tips', '', '']);
  wsInstr.addRow(['• Delete the sample row before importing your real data.', '', '']);
  wsInstr.addRow(['• You can add extra columns — they will be ignored.', '', '']);
  wsInstr.addRow(['• Rows missing a required field will be skipped with an error.', '', '']);

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(
    buffer,
    'equipment_import_template.xlsx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}

export async function downloadPartsTemplate() {
  const sample = [
    {
      part_number: 'BRG-6205',
      name: 'Deep Groove Ball Bearing 6205',
      description: 'Standard bearing for pump motors',
      location: 'Shelf A-3',
      supplier: 'SKF',
      stock_quantity: '12',
      reorder_threshold: '4',
      unit_cost: '8.50',
    },
  ];
  await exportToExcel(sample, 'parts_import_template');
}

export async function downloadMaintenanceTaskTemplate() {
  const wb = new ExcelJS.Workbook();

  // Sheet 1: data with sample rows
  const wsData = wb.addWorksheet('PM Schedules');
  wsData.columns = [
    { header: 'equipment_ref',              key: 'equipment_ref',              width: 16 },
    { header: 'name',                       key: 'name',                       width: 28 },
    { header: 'frequency_phrase',           key: 'frequency_phrase',           width: 18 },
    { header: 'task_type',                  key: 'task_type',                  width: 12 },
    { header: 'technician_ref',             key: 'technician_ref',             width: 20 },
    { header: 'estimated_duration_minutes', key: 'estimated_duration_minutes', width: 12 },
    { header: 'instructions',               key: 'instructions',               width: 36 },
    { header: 'last_completed_at',          key: 'last_completed_at',          width: 14 },
    { header: 'next_due_at',                key: 'next_due_at',                width: 14 },
    { header: 'is_active',                  key: 'is_active',                  width: 10 },
  ];
  wsData.addRow({
    equipment_ref:    'PUMP-001',
    name:             'Lube bearings',
    frequency_phrase: 'monthly',
    task_type:        'preventive',
    technician_ref:   '',
    estimated_duration_minutes: 30,
    instructions:     'Apply grease per manufacturer spec.',
    last_completed_at:'',
    next_due_at:      '',
    is_active:        'true',
  });
  wsData.addRow({
    equipment_ref:    'SN-12345',
    name:             'Belt inspection',
    frequency_phrase: 'quarterly',
    task_type:        'preventive',
    technician_ref:   'bob@plant.com',
    estimated_duration_minutes: 15,
    instructions:     '',
    last_completed_at:'',
    next_due_at:      '',
    is_active:        'true',
  });
  wsData.addRow({
    equipment_ref:    'PUMP-002',
    name:             'Hour-meter overhaul',
    frequency_phrase: 'every 500 hours',
    task_type:        'preventive',
    technician_ref:   '',
    estimated_duration_minutes: 240,
    instructions:     '',
    last_completed_at:'',
    next_due_at:      '',
    is_active:        'true',
  });

  // Sheet 2: instructions / valid values
  const wsInstr = wb.addWorksheet('Instructions');
  wsInstr.columns = [
    { header: 'Column',               width: 22 },
    { header: 'Required?',            width: 10 },
    { header: 'Valid Values / Notes', width: 80 },
  ];
  const instructions: [string, string, string][] = [
    ['equipment_ref',    'Required', 'Asset tag, serial number, or unique equipment name'],
    ['name',             'Required', 'Task name e.g. "Lube bearings"'],
    ['frequency_phrase', 'Required', 'daily | weekly | monthly | quarterly | annually | "every N days/weeks/months" | "every N hours" | "on condition"'],
    ['task_type',        'Optional', 'preventive | predictive | corrective (defaults to preventive)'],
    ['technician_ref',   'Optional', 'Email or full name of assigned technician (leave blank to assign later)'],
    ['estimated_duration_minutes', 'Optional', 'Integer minutes'],
    ['instructions',     'Optional', 'Free text — checklist or notes'],
    ['last_completed_at','Optional', 'YYYY-MM-DD or MM/DD/YYYY'],
    ['next_due_at',      'Optional', 'YYYY-MM-DD or MM/DD/YYYY'],
    ['is_active',        'Optional', 'true | false (defaults to true)'],
  ];
  for (const row of instructions) wsInstr.addRow(row);
  wsInstr.addRow([]);
  wsInstr.addRow(['Tips', '', '']);
  wsInstr.addRow(['• Delete the sample rows before importing your real data.', '', '']);
  wsInstr.addRow(['• You can add extra columns — they will be ignored.', '', '']);
  wsInstr.addRow(['• Equipment must already exist in VantixMT before importing PMs.', '', '']);

  const buffer = await wb.xlsx.writeBuffer();
  downloadBuffer(
    buffer,
    'pm_schedules_import_template.xlsx',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  );
}
